import React, { useEffect, useState, useRef, useMemo } from 'react';
import swal from 'sweetalert2';

import { getStrokeExtraShot, getPlayerScore, getPercentage, getAdjustedHandicap } from '../tournamentHelper';
import { deleteScorecard, postScoreCard, sendBetterballGreensomeScorecardMail } from '../../api/tournamentAPI';

// Holes 0-8  => "front" nine (Holes 1-9) | Holes 9-17 => "back" nine  (Holes 10-18)
// Which format (Betterball / Greensome) applies to which nine is controlled by `frontFormat` state below, and can be swapped with the toggle in the header.
const FRONT_HOLES = Array.from({ length: 9 }, (_, i) => i);      // 0..8
const BACK_HOLES  = Array.from({ length: 9 }, (_, i) => i + 9);  // 9..17

// `format.RoundFormat` (when present) carries per-format handicap/min-drive config, e.g.: format.RoundFormat for matched Tournament Format List else Default Format Present in tournamentDraw
const getRoundFormatConfig = (roundFormatList, formatName, fallbackDraw) => {
    const match = (roundFormatList || []).find(
        (rf) => (rf.TournamentRoundFormat || '').toLowerCase() === formatName.toLowerCase()
    );
    if (match) return match;

    if (fallbackDraw) {
        return {
            TournamentRoundFormat: formatName,
            Lower: fallbackDraw.Lower,
            LowerMiddle: 0,
            Middle: 0,
            Higher: fallbackDraw.Higher,
            MinDrive: fallbackDraw.MinDrive,
            IsMin: !!fallbackDraw?.MinDrive ? 1 : 0,
        };
    }
    return null;
};

const BetterballGreensome = ({
    playerList,
    tournamentDetails,
    tournamentDraw,
    tournamentRound,
    selectedPlayer,
    gameDetails,
    currentRound,
    format,
    scorecardStatus,
    setScorecardStatus,
    pointData,
    setShowPointModal,
    closeDrawer,
    fetchTournamentLeaderboard
}) => {

    // 'betterball' | 'greensome' — which format the FRONT nine (holes 1-9) plays.
    // The BACK nine (holes 10-18) always plays the other format.
    const [frontFormat, setFrontFormat] = useState('betterball');
    const backFormat = frontFormat === 'betterball' ? 'greensome' : 'betterball';

    const [scores,               setScores]               = useState(null);
    const [totalScores,          setTotalScores]          = useState([]);
    const [footerFrontScore,     setFooterFrontScore]     = useState('-');
    const [footerBackScore,      setFooterBackScore]      = useState('-');
    const [footerTotalScore,     setFooterTotalScore]     = useState('-');
    const [comment,              setComment]              = useState("");

    // Betterball handicaps (individual, applied on betterball holes)
    const [calculatedHandicap1,  setCalculatedHandicap1]  = useState();
    const [calculatedHandicap2, setCalculatedHandicap2] = useState();
    
    // Greensome handicap (combined, applied on greensome holes)
    const [calculatedHandicapCombined, setCalculatedHandicapCombined] = useState();

    const [userTotal,      setUserTotal]      = useState(0); // greensome gross total
    const [totalNetScore,  setTotalNetScore]  = useState(0); // greensome net total
    
    const [minBetterballHolesPerPlayer, setMinBetterballHolesPerPlayer] = useState(0); // greensome net total
    
    const inputRefs = useRef([]);

    //#region Helpers - format per hole
    const getFormatForHole = (index) => (FRONT_HOLES.includes(index) ? frontFormat : backFormat);

    // Per-format handicap / min-drive configuration, sourced from format.RoundFormat.
    // Betterball holes always use the Betterball row's Lower/Higher percentages
    // (whether that format is currently playing on the front or back nine), and
    // Greensome holes always use the Greensome row's Lower/Higher/MinDrive/IsMin.
    const betterballFormatData = useMemo(
        () => getRoundFormatConfig(format?.RoundFormat, 'Betterball', tournamentDraw),
        [format, tournamentDraw]
    );
    const greensomeFormatData = useMemo(
        () => getRoundFormatConfig(format?.RoundFormat, 'Greensome', tournamentDraw),
        [format, tournamentDraw]
    );

    const greensomePointData = (pointData || []).filter((p) => p.PointName === 'Greensome')?.sort((a, b) => b.Score - a.Score);

    const betterballPointData = (pointData || []).filter((p) => p.PointName === 'Fourball Betterball')?.sort((a, b) => b.Score - a.Score);
    //#endregion

    //#region Effects
    useEffect(() => {
        const initialScores = {};

        if (!!tournamentDraw && !!selectedPlayer) {
            const Handicap1 = Math.round(getPercentage(
                selectedPlayer.Handicap,
                selectedPlayer.Player1Gender == 'Female' ? betterballFormatData?.Lower : betterballFormatData?.Higher
            ));
            setMinBetterballHolesPerPlayer(betterballFormatData?.MinDrive || 0)
            setCalculatedHandicap1(Handicap1);

            const Handicap2 = Math.round(getPercentage(
                selectedPlayer.Player2Handicap,
                selectedPlayer.Player2Gender == 'Female' ? betterballFormatData?.Lower : betterballFormatData?.Higher
            ));
            setCalculatedHandicap2(Handicap2);

            const HandicapCombined = getAdjustedHandicap(
                greensomeFormatData?.Higher,
                greensomeFormatData?.Lower,
                selectedPlayer.Handicap,
                selectedPlayer.Player2Handicap
            );
            setCalculatedHandicapCombined(HandicapCombined);
        }

        if (gameDetails.Card.length > 0) {
            gameDetails.Card.forEach((item) => {
                const index = item.HoleValue - 1;
                const holeFormat = getFormatForHole(index);
                const existingScore = initialScores[index];
                let newScore;

                if (holeFormat === 'betterball') {
                    const scoreKey = item.PlayerID === selectedPlayer.PlayerID ? 'player1Score' : 'player2Score';
                    newScore = { [scoreKey]: item.Scratch == "Y" ? "X" : item.Score };
                } else {
                    newScore = {
                        player1Score: item.Scratch == "Y" ? "X" : item.Score,
                        driveUserID: item.DriveUserID || selectedPlayer.PlayerID
                    };
                }

                initialScores[index] = (!!existingScore) ? { ...existingScore, ...newScore } : newScore;
            });

            for (let i = 0; i < 18; i++) {
                const holeFormat = getFormatForHole(i);
                if (!initialScores[i]) {
                    initialScores[i] = holeFormat === 'betterball'
                        ? { player1Score: 0, player2Score: 0 }
                        : { player1Score: 0, driveUserID: selectedPlayer.PlayerID };
                } else if (holeFormat === 'betterball') {
                    if (!initialScores[i].hasOwnProperty('player1Score')) initialScores[i].player1Score = 0;
                    if (!initialScores[i].hasOwnProperty('player2Score')) initialScores[i].player2Score = 0;
                } else {
                    if (!initialScores[i].hasOwnProperty('player1Score')) initialScores[i].player1Score = 0;
                    if (!initialScores[i].hasOwnProperty('driveUserID')) initialScores[i].driveUserID = selectedPlayer.PlayerID;
                }
            }
        } else {
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const holeFormat = getFormatForHole(index);
                initialScores[index] = holeFormat === 'betterball'
                    ? { player1Score: 0, player2Score: 0 }
                    : { player1Score: 0, driveUserID: selectedPlayer.PlayerID };
            });
        }

        if (!!gameDetails?.Result) {
            let scStatus = (gameDetails?.Result.IsVerified == 'Y' ? 'verify' : (gameDetails?.Result.IsVerified == 'DQ' ? 'disqualify' : (gameDetails?.Result.IsVerified == 'WD' ? 'withdraw' : 'N')));
            setScorecardStatus(scStatus);
            setComment(gameDetails?.Result.Comment);
        }

        setScores(initialScores);
    }, [gameDetails, tournamentDraw, frontFormat, betterballFormatData, greensomeFormatData]);

    useEffect(() => {
        if (scores) calculateScores(scores);
    }, [scores, calculatedHandicap1, calculatedHandicap2, calculatedHandicapCombined, frontFormat]);

    useEffect(() => {
        if (selectedPlayer) {
            setScorecardStatus(selectedPlayer.IsVerified == 'Y' ? "verify" : selectedPlayer.IsVerified == "WD" ? 'withdraw' : selectedPlayer.IsVerified == 'DQ' ? 'disqualify' : '');
        }
    }, [selectedPlayer]);
    //#endregion

    //#region API's
    const deleteScoreCard = async () => {
        try {
            swal.fire({
                titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false,
                onOpen: () => { swal.showLoading() }
            });

            await deleteScorecard(selectedPlayer.DrawID);
            fetchTournamentLeaderboard();
            swal.fire({
                icon: 'success', timer: 1500, animation: false, showConfirmButton: false,
                onOpen: () => swal.hideLoading(),
                titleText: 'Scorecard Updated Successfully!',
            });
            closeDrawer();
        } catch (err) {
            swal.fire({
                icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand',
                titleText: 'Error!', text: err.message,
                onOpen: () => swal.hideLoading()
            });
        }
    }

    const postScorecard = async (scoreCard) => {
        try {
            swal.fire({
                titleText: 'Loading', showConfirmButton: false,
                text: 'Please Wait...', onOpen: () => { swal.showLoading() }
            });

            await postScoreCard(scoreCard);
            fetchTournamentLeaderboard();
            swal.fire({
                icon: 'success', timer: 1500, animation: false, showConfirmButton: false,
                onOpen: () => swal.hideLoading(),
                titleText: 'Scorecard Updated Successfully!',
            });
            closeDrawer();
        } catch (err) {
            swal.fire({
                icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand',
                titleText: 'Error!', text: err.message,
                onOpen: () => swal.hideLoading()
            });
        }
    }
    //#endregion

    //#region Helpers
    const handleDriveUserChange = (index, value) => {
        setScores(s => ({ ...s, [index]: { ...s[index], driveUserID: value } }));
    }

    // ============================================================================
    // Betterball "min-3" allocation - split into small, pure, independently
    // testable functions (no React state closures). calculateScores() below just
    // wires these together using values pulled from props/state.
    // ============================================================================

    // Computes gross/net score + Stableford-style point for ONE player on ONE
    // hole. This was duplicated three times (player1 betterball, player2
    // betterball, player1 greensome) in the old function - now it's one place.
    const computePlayerHolePoints = (playerInput, handicap, item, pointFor) => {
        const score = getPlayerScore(playerInput, handicap, item.Stroke, item.Par);
        let point = 0;
        if (score.grossScore != 0) {
            const adj = score.IsScratch ? score.netScore : (score.netScore - item.Par);
            point = pointFor(adj);
        }
        return { ...score, point };
    };

    // Builds the RAW (not-yet-decided) data for one betterball hole. Nothing here
    // decides who counts - that needs to see the whole nine at once, which is
    // what naturalAssignment/fillMinimumQuota below do.
    const stageBetterballHole = (index, item, hole, calculatedHandicap1, calculatedHandicap2, pointFor) => {
        const p1 = computePlayerHolePoints(hole.player1Score || 0, calculatedHandicap1, item, pointFor);
        const p2 = computePlayerHolePoints(hole.player2Score || 0, calculatedHandicap2, item, pointFor);

        return {
            index, HoleNo: item.HoleNoValue,
            p1, p2,
            player1Point: p1.point, player2Point: p2.point,
            // A hole only counts toward anyone's quota once BOTH partners have
            // actually posted a score - an in-progress or scratched hole can't
            // force a swap either way.
            isPlayed: p1.grossScore != 0 && p2.grossScore != 0,
        };
    };

    // Builds the finished entry for one greensome hole, plus the gross/net
    // numbers to fold into the running greensome totals. Greensome has nothing
    // to "decide" - one combined score per hole - so this is fully self-contained.
    const buildGreensomeEntry = (item, hole, calculatedHandicapCombined, pointFor, selectedPlayer) => {
        const p1 = computePlayerHolePoints(hole.player1Score || 0, calculatedHandicapCombined, item, pointFor);

        const entry = {
            HoleNo: item.HoleNoValue, format: 'greensome',
            player1GrossScore: p1.grossScore, player2GrossScore: null,
            player1NetScore: p1.netScore, player2NetScore: null,
            player1Point: p1.point, player2Point: null,
            maxTotal: p1.point,
            player1Scratch: p1.IsScratch, player2Scratch: null,
            driveUserID: hole.driveUserID || selectedPlayer.PlayerID,
        };

        return { entry, grossScore: p1.grossScore, netScore: p1.netScore };
    };

    const countCreditedHoles = (holes, player) =>
        holes.filter((h) => h.isPlayed && h.countedPlayer === player).length;

    // Pass 2a - natural pick, hole by hole, in hole order:
    //   - Whoever scored the better (higher) points counts - same idea as plain
    //     betterball's Math.max, at zero cost to the team.
    //   - Tie handling: if the points are equal, don't default to player1 - give
    //     the hole to whichever partner has fewer credited holes SO FAR (running
    //     tally as we walk the nine). This spreads ties out live as scores are
    //     entered instead of silently stacking every tie on one player.
    const naturalAssignment = (holes) => {
        let runningP1 = 0, runningP2 = 0;

        holes.forEach((h) => {
            if (h.player1Point > h.player2Point) {
                h.countedPlayer = 'player1';
            } else if (h.player2Point > h.player1Point) {
                h.countedPlayer = 'player2';
            } else {
                h.countedPlayer = runningP1 <= runningP2 ? 'player1' : 'player2';
            }

            if (h.isPlayed) {
                if (h.countedPlayer === 'player1') runningP1++; else runningP2++;
            }
        });

        return holes;
    };

    // Pass 2b - automatic quota fill: if a player ends up credited on fewer than
    // `minPerPlayer` PLAYED holes, pull holes back from the partner automatically
    // - cheapest (smallest point gap) holes first, so the team gives up the
    // fewest points possible. A tied hole (gap 0) is free to reassign and is
    // always taken first.
    const fillMinimumQuota = (holes, minPerPlayer) => {
        const fillFor = (deficientPlayer, otherPlayer) => {
            const deficit = minPerPlayer - countCreditedHoles(holes, deficientPlayer);
            if (deficit <= 0) return;

            const candidates = holes
                .filter((h) => h.isPlayed && h.countedPlayer === otherPlayer)
                .map((h) => ({ hole: h, gap: Math.abs(h.player1Point - h.player2Point) }))
                .sort((a, b) => a.gap - b.gap);

            for (let i = 0; i < deficit && i < candidates.length; i++) {
                candidates[i].hole.countedPlayer = deficientPlayer;
            }
        };

        // Settle whichever player is more short first, so a partial round (fewer
        // than 6 holes played yet) resolves as fairly as the data allows.
        const p1Deficit = minPerPlayer - countCreditedHoles(holes, 'player1');
        const p2Deficit = minPerPlayer - countCreditedHoles(holes, 'player2');

        if (p1Deficit >= p2Deficit) {
            fillFor('player1', 'player2');
            fillFor('player2', 'player1');
        } else {
            fillFor('player2', 'player1');
            fillFor('player1', 'player2');
        }

        return holes;
    };

    // Public entry point combining 2a + 2b - decide who counts on every hole of
    // one betterball nine, guaranteeing each player is credited on at least
    // `minPerPlayer` PLAYED holes wherever that's mathematically possible.
    const assignBetterballCountedScores = (holes, minPerPlayer = minBetterballHolesPerPlayer) => {
        naturalAssignment(holes);
        fillMinimumQuota(holes, minPerPlayer);
        return holes;
    };

    // Pass 3 - turn one decided betterball hole into its final scorecard entry.
    const buildBetterballEntry = (h) => {
        const totalScore = h.countedPlayer === 'player1' ? h.player1Point : h.player2Point;
        const countedPlayer = h.isPlayed ? h.countedPlayer : null;
        return {
            HoleNo: h.HoleNo, format: 'betterball',
            player1GrossScore: h.p1.grossScore, player2GrossScore: h.p2.grossScore,
            player1NetScore: h.p1.netScore, player2NetScore: h.p2.netScore,
            player1Point: h.player1Point, player2Point: h.player2Point,
            maxTotal: totalScore,
            player1Scratch: h.p1.IsScratch, player2Scratch: h.p2.IsScratch,
            countedPlayer,
        };
    };

    // Final pass - sum OUT/IN footer totals from the finished per-hole entries.
    const aggregateFooterTotals = (scoreCard, entries) => {
        let cumulativeFront = 0, cumulativeBack = 0;
        let allFrontZero = true, allBackZero = true;

        scoreCard.forEach((item, index) => {
            const totalScore = entries[index]?.maxTotal || 0;
            if (totalScore != 0) {
                if (FRONT_HOLES.includes(index)) { allFrontZero = false; cumulativeFront += totalScore; }
                else { allBackZero = false; cumulativeBack += totalScore; }
            }
        });

        return { cumulativeFront, cumulativeBack, allFrontZero, allBackZero };
    };

    // ============================================================================
    // Inside the component:
    // ============================================================================
    const calculateScores = (updatedScores) => {
        // const maxScore = pointData[0]?.Score || 0;
        // const minScore = pointData[pointData.length - 1]?.Score || 0;

        // const pointFor = (adjScore) => {
        //     let clamped = adjScore > maxScore ? maxScore : adjScore;
        //     clamped = clamped < minScore ? minScore : clamped;
        //     const match = $.grep(pointData, (s) => s.Score === clamped)[0];
        //     return match ? match.Point : 0;
        // };

        const createPointFor = (sortedPoints) => {
            const maxScore = sortedPoints[0]?.Score || 0;
            const minScore = sortedPoints[sortedPoints.length - 1]?.Score || 0;

            return (adjScore) => {
                let clamped = adjScore;
                if (clamped > maxScore) clamped = maxScore;
                if (clamped < minScore) clamped = minScore;
                const match = $.grep(sortedPoints, (s) => s.Score === clamped)[0];
                return match ? match.Point : 0;
            };
        };

        const greensomePointFor = createPointFor(greensomePointData);
        const betterballPointFor = createPointFor(betterballPointData);

        const betterballHoles = [];
        const finishedEntries = {};
        let cumulativeUserTotal = 0, cumulativeNetTotal = 0;

        // Step 1: stage every hole. Betterball holes wait (nothing to decide
        // yet); greensome holes are finished immediately.
        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const holeFormat = getFormatForHole(index);
            const hole = updatedScores[index] || {};

            if (holeFormat === 'betterball') {
                betterballHoles.push(
                    stageBetterballHole(index, item, hole, calculatedHandicap1, calculatedHandicap2, betterballPointFor)
                );
            } else {
                const { entry, grossScore, netScore } = buildGreensomeEntry(
                    item, hole, calculatedHandicapCombined, greensomePointFor, selectedPlayer
                );
                finishedEntries[index] = entry;
                cumulativeUserTotal += grossScore;
                cumulativeNetTotal += netScore;
            }
        });

        // Step 2: decide who counts on every betterball hole, across the whole
        // nine at once (min-3 rule + tie-break + auto quota fill).
        assignBetterballCountedScores(betterballHoles, minBetterballHolesPerPlayer);

        // Step 3: turn the decided betterball holes into finished entries.
        betterballHoles.forEach((h) => {
            finishedEntries[h.index] = buildBetterballEntry(h);
        });

        const updatedTotalScores = gameDetails.TeeBoxes.ScoreCard.map((_, index) => finishedEntries[index]);

        // Step 4: sum the OUT/IN footer totals from the finished entries.
        const { cumulativeFront, cumulativeBack, allFrontZero, allBackZero } =
            aggregateFooterTotals(gameDetails.TeeBoxes.ScoreCard, finishedEntries);

        setTotalScores(updatedTotalScores);
        setUserTotal(cumulativeUserTotal);
        setTotalNetScore(cumulativeNetTotal);
        setFooterFrontScore(allFrontZero ? '-' : cumulativeFront);
        setFooterBackScore(allBackZero ? '-' : cumulativeBack);
        setFooterTotalScore((allFrontZero && allBackZero) ? '-' : (cumulativeFront + cumulativeBack));
    };

    const handleScoreChange = (playerKey, index, value) => {
        if (!(/^[0-9X]*$/).test(value)) return;

        const oldScore = scores[index];
        const updatedScores = { ...scores, [index]: { ...oldScore, [playerKey]: value } };
        setScores(updatedScores);

        // Move focus to next relevant input
        const holeFormat = getFormatForHole(index);
        let nextKey;
        if (holeFormat === 'betterball' && playerKey === 'player1Score') {
            nextKey = `${index}_player2Score`;
        } else {
            nextKey = `${index + 1}_player1Score`;
        }
        if (inputRefs.current[nextKey]) {
            inputRefs.current[nextKey].focus();
            inputRefs.current[nextKey].select();
        }
    };

    const toggleStatus = (value) => setScorecardStatus(prevStatus => (prevStatus == value ? '' : value));

    const swapFormats = () => {
        setFrontFormat(prev => (prev === 'betterball' ? 'greensome' : 'betterball'));
        setScores(null); // force re-init on the new hole/format mapping
    };

    const saveScorecard = () => {
        // Min-drive requirement always comes from the Greensome row of RoundFormat
        // (falls back to tournamentDraw.MinDrive if that row isn't configured).
        const minDrive = greensomeFormatData?.MinDrive ?? tournamentDraw.MinDrive;
        const isMinDriveEnforced = greensomeFormatData ? !!greensomeFormatData.IsMin : true;

        const drivePlayer1 = [];
        const drivePlayer2 = [];
        let driveUser = '';
        let notGoodMinDrive = false;

        let newScoreCard = [];
        let notComplete = false;
        let hole = 0;

        const player1 = { id: selectedPlayer.PlayerID, name: selectedPlayer.Player1 };
        const player2 = { id: selectedPlayer.Player2ID, name: selectedPlayer.Player2 };

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const holeFormat = getFormatForHole(index);
            const cHole = item.HoleNoValue;
            let cScore = totalScores.find(t => t.HoleNo == cHole);

            if (holeFormat === 'betterball') {
                if (((isNaN(cScore.player1GrossScore) || cScore.player1GrossScore == 0) || (isNaN(cScore.player2GrossScore) || cScore.player2GrossScore == 0)) && scorecardStatus == 'verify') {
                    notComplete = true;
                    return;
                }

                newScoreCard.push({
                    ID: 0, TournamentDrawID: selectedPlayer.DrawID,
                    PlayerID: selectedPlayer.PlayerID, PlayerType: selectedPlayer.Player1Type,
                    ScoreCardMasterID: item.ID,
                    Score: cScore.player1GrossScore, HandicapScore: cScore.player1NetScore,
                    DriveUserID: 0, Point: cScore.player1Point, TotalPoint: cScore.player1Point,
                    Scratch: (cScore.player1Scratch) ? "Y" : "N",
                });

                newScoreCard.push({
                    ID: 0, TournamentDrawID: selectedPlayer.DrawID,
                    PlayerID: selectedPlayer.Player2ID, PlayerType: selectedPlayer.Player2Type,
                    ScoreCardMasterID: item.ID,
                    Score: cScore.player2GrossScore, HandicapScore: cScore.player2NetScore,
                    DriveUserID: 0, Point: cScore.player2Point, TotalPoint: cScore.player2Point,
                    Scratch: (cScore.player2Scratch) ? "Y" : "N",
                });

                if (cScore.player1GrossScore != 0 || cScore.player2GrossScore != 0) hole++;
            } else {
                if ((isNaN(cScore.player1GrossScore) || cScore.player1GrossScore == 0) && scorecardStatus == 'verify') {
                    notComplete = true;
                    return;
                }

                const driveId = cScore.driveUserID;
                const driveName = driveId == player1.id ? player1.name : player2.name;
                const drive = { id: driveId, name: driveName };

                newScoreCard.push({
                    ID: 0, TournamentDrawID: selectedPlayer.DrawID,
                    PlayerID: selectedPlayer.PlayerID, PlayerType: selectedPlayer.Player1Type,
                    ScoreCardMasterID: item.ID,
                    Score: cScore.player1GrossScore, HandicapScore: cScore.player1NetScore,
                    DriveUserID: drive.id, Point: cScore.player1Point, TotalPoint: cScore.player1Point,
                    Scratch: (cScore.player1Scratch) ? "Y" : "N",
                });

                if (cScore.player1GrossScore != 0) hole++;

                if (drive.id == player1.id) drivePlayer1.push(drive);
                else drivePlayer2.push(drive);
            }
        });

        if (notComplete) {
            swal.fire({ icon: "warning", titleText: "Incomplete Scorecard", text: "Please complete the scorecard..!" });
            return;
        }

        // Min-drive check only applies across the greensome holes actually played,
        // and only when the Greensome format row has IsMin enabled.
        const greensomeHolesCount = drivePlayer1.length + drivePlayer2.length;
        if (greensomeHolesCount > 0 && isMinDriveEnforced && minDrive > 0) {
            if (drivePlayer1.length < minDrive) driveUser = `"${player1.name}"`;
            if (drivePlayer2.length < minDrive) driveUser += `"${player2.name}"`;
            if (drivePlayer1.length < minDrive || drivePlayer2.length < minDrive) notGoodMinDrive = true;
        }

        if (notGoodMinDrive) {
            swal.fire({ icon: "warning", titleText: "Invalid Drive", text: `The player ${driveUser} should get at least ${minDrive} min drive` });
            return;
        }

        const result = {
            ID: 0, Hole: hole, TournamentDrawID: selectedPlayer.DrawID,
            Point: footerTotalScore == '-' ? 0 : footerTotalScore,
            TotalPoint: footerTotalScore == '-' ? 0 : footerTotalScore,
            IsVerified: (scorecardStatus == 'verify') ? 'Y' : (scorecardStatus == 'disqualify' ? 'DQ' : (scorecardStatus == 'withdraw' ? 'WD' : 'N')),
            Comment: (scorecardStatus == 'disqualify' || scorecardStatus == 'withdraw') ? comment : '',
            FrontFormat: frontFormat, BackFormat: backFormat
        };

        const dto = { scoreCard: newScoreCard, result: result };
        postScorecard(dto);
    }

    const sendScorecard = async () => {
        try {
            swal.fire({ showConfirmButton: false, onOpen: () => swal.showLoading(), titleText: 'Loading', text: 'Please Wait...', allowOutsideClick: false, allowEscapeKey: false });

            let playerNameList = [];
            playerNameList.push({ ID: 1, Name: playerList["Player1"].UserName, Email: playerList["Player1"].Email });
            playerNameList.push({ ID: 2, Name: playerList["Player2"].UserName, Email: playerList["Player2"].Email });

            let scoreCardList = [];
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const cHole = item.HoleNoValue;
                let cScore = totalScores.find(t => t.HoleNo == cHole);
                const driveName = cScore.format === 'greensome'
                    ? (cScore.driveUserID == selectedPlayer.PlayerID ? selectedPlayer.Player1 : selectedPlayer.Player2)
                    : '-';

                scoreCardList.push({
                    Hole: cHole, Par: item.Par, Stroke: item.Stroke, Format: cScore.format,
                    Drive: driveName,
                    Player1GrossScore: cScore.player1GrossScore, Player1NetScore: cScore.player1NetScore, Player1Point: cScore.player1Point,
                    Player2GrossScore: cScore.player2GrossScore, Player2NetScore: cScore.player2NetScore, Player2Point: cScore.player2Point,
                    DriveUserID: cScore.driveUserID || null,
                    CountedPlayer: cScore.countedPlayer,
                    Total: cScore.maxTotal
                });
            });

            const tournament = {
                ID: tournamentDetails.ID, Name: tournamentDetails.Name,
                Format: format?.Format, Round: selectedPlayer.Round, Date: tournamentDetails.StartDate
            };

            await sendBetterballGreensomeScorecardMail(
                playerNameList,
                selectedPlayer.Player1, selectedPlayer.Player2,
                scoreCardList, gameDetails.TeeBoxes.Par,
                {calculatedHandicap1, calculatedHandicap2, calculatedHandicapCombined},
                footerFrontScore, footerBackScore, footerTotalScore,
                tournament
            );

            swal.close();
            swal.fire({ icon: 'success', titleText: 'Scorecard Email Sent', showConfirmButton: false, timer: 1500 });
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message, onOpen: () => swal.hideLoading() });
        }
    }
    //#endregion

    //#region Render helpers
    const renderTableHead = (holeFormat) => {
        
        if (holeFormat === 'betterball') {
            const player1Marked = (selectedPlayer?.Player1Gender == 'Female' && betterballFormatData?.Lower != 100) || (betterballFormatData?.Higher != 100);
            const player2Marked = (selectedPlayer?.Player2Gender == 'Female' && betterballFormatData?.Lower != 100) || (betterballFormatData?.Higher != 100);

            return (<>
                <tr>
                    <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Hole</th>
                    <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Par</th>
                    <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Stroke</th>
                    <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {selectedPlayer.Player1} ({calculatedHandicap1 < 0 ? "+" + Math.abs(calculatedHandicap1) : calculatedHandicap1}) {player1Marked ? '*' : ''}
                    </th>
                    <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {selectedPlayer.Player2} ({calculatedHandicap2 < 0 ? "+" + Math.abs(calculatedHandicap2) : calculatedHandicap2}) {player2Marked ? '*' : ''}
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Total Points</th>
                </tr>
                <tr>
                    <th className="text-center">Gross Score</th><th className="text-center">Net Score</th><th className="text-center">Points</th>
                    <th className="text-center">Gross Score</th><th className="text-center">Net Score</th><th className="text-center">Points</th>
                </tr>
            </>);
        }

        const isPlayersMarked = (greensomeFormatData?.Lower != 100) || (greensomeFormatData?.Higher != 100);
        return (<>
            <tr>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Hole</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Par</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Stroke</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '15%', verticalAlign: 'middle' }}>Drive</th>
                <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {selectedPlayer.Player1Nick || selectedPlayer.Player1} | {selectedPlayer.Player2Nick || selectedPlayer.Player2} ({calculatedHandicapCombined}){isPlayersMarked ? '*' : ''}
                </th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Points</th>
            </tr>
            <tr>
                <th className="text-center" style={{ textAlign: 'center', width: '15%', verticalAlign: 'middle' }}>Gross Score</th>
                <th className="text-center" style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Net Score</th>
                <th className="text-center" style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Points</th>
            </tr>
        </>);
    };

    const renderRow = (index, item) => {
        if (!scores || !scores[index]) return null;
        const holeFormat = getFormatForHole(index);
        const totalScore = totalScores[index] || {};

        if (holeFormat === 'betterball') {
            const player1Score = scores[index]?.player1Score || 0;
            const player2Score = scores[index]?.player2Score || 0;
            const player1ExtraShot = (getStrokeExtraShot(item.Stroke, calculatedHandicap1) || 0);
            const player2ExtraShot = (getStrokeExtraShot(item.Stroke, calculatedHandicap2) || 0);

            const player1Counted = totalScore?.countedPlayer === 'player1';
            const player2Counted = totalScore?.countedPlayer === 'player2';

            return (
                <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                    <td className='text-center'>{item.HoleNoValue}</td>
                    <td className='text-center'>{item.Par}</td>
                    <td className='text-center'>{item.Stroke}</td>
                    <td className="player-td" data-extra={player1ExtraShot}>
                        <div className="input-group">
                            <input className="form-control input-sm input-numeric" type="text" value={player1Score}
                                ref={el => inputRefs.current[`${index}_player1Score`] = el}
                                onChange={(e) => handleScoreChange('player1Score', index, parseInt(e.target.value))} />
                            <div className="input-group-append">
                                <span className="input-group-text clickable" onClick={() => handleScoreChange('player1Score', index, "X")}><i className="la la-times"></i></span>
                            </div>
                        </div>
                    </td>
                    <td className="card-score text-center">{totalScore.player1NetScore}</td>
                    {/* <td className="card-point text-center">{totalScore.player1Point}</td> */}
                    <td className={`card-point text-center${player1Counted ? ' bg-light-success font-weight-bold' : ''}`} title={player1Counted ? "This player's score counted for the team" : undefined}>
                        {player1Counted ? (
                            <span className="badge badge-success font-weight-bold">
                                {totalScore.player1Point}
                            </span>
                        ) : (
                            totalScore.player1Point
                        )}
                    </td>
                    <td className="player-td" data-extra={player2ExtraShot}>
                        <div className="input-group">
                            <input className="form-control input-sm input-numeric" type="text" value={player2Score}
                                ref={el => inputRefs.current[`${index}_player2Score`] = el}
                                onChange={(e) => handleScoreChange('player2Score', index, parseInt(e.target.value))} />
                            <div className="input-group-append">
                                <span className="input-group-text clickable" onClick={() => handleScoreChange('player2Score', index, "X")}><i className="la la-times"></i></span>
                            </div>
                        </div>
                    </td>
                    <td className="card-score text-center">{totalScore.player2NetScore}</td>
                    {/* <td className="card-point text-center">{totalScore.player2Point}</td> */}
                    <td className={`card-point text-center${player2Counted ? ' bg-light-success font-weight-bold' : ''}`} title={player2Counted ? "This player's score counted for the team" : undefined}>
                        {player2Counted ? (
                            <span className="badge badge-success font-weight-bold">
                                {totalScore.player2Point}
                            </span>
                        ) : (
                            totalScore.player2Point
                        )}
                    </td>
                    <td className="card-total-score text-center">{totalScore.maxTotal}</td>
                </tr>
            );
        }

        const player1Score = scores[index]?.player1Score || 0;
        const player1ExtraShot = (getStrokeExtraShot(item.Stroke, calculatedHandicapCombined) || 0);
        const driveUserID = scores[index]?.driveUserID || selectedPlayer.PlayerID;

        return (
            <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                <td className='text-center'>{item.HoleNoValue}</td>
                <td className='text-center'>{item.Par}</td>
                <td className='text-center'>{item.Stroke}</td>
                <td>
                    <select className="form-drive form-control" tabIndex="-1"
                        onChange={(e) => handleDriveUserChange(index, e.target.value)}
                        value={driveUserID}>
                        <option value={selectedPlayer.PlayerID}>{selectedPlayer.Player1}</option>
                        <option value={selectedPlayer.Player2ID}>{selectedPlayer.Player2}</option>
                    </select>
                </td>
                <td className="player-td" data-extra={player1ExtraShot}>
                    <div className="input-group">
                        <input className="form-control input-sm input-numeric" type="text" value={player1Score}
                            ref={el => inputRefs.current[`${index}_player1Score`] = el}
                            onChange={(e) => handleScoreChange('player1Score', index, parseInt(e.target.value))} />
                        <div className="input-group-append">
                            <span className="input-group-text clickable" onClick={() => handleScoreChange('player1Score', index, "X")}><i className="la la-times"></i></span>
                        </div>
                    </div>
                </td>
                <td className="card-score text-center">{totalScore.player1NetScore}</td>
                <td className="card-point text-center">{totalScore.player1Point}</td>
                <td className="card-total-score text-center">{totalScore.maxTotal}</td>
            </tr>
        );
    };

    const renderNineTable = (holeIndices, label, footerScore) => {
        if (!gameDetails || !scores) return null;
        const holeFormat = getFormatForHole(holeIndices[0]);
        const colCount = holeFormat === 'betterball' ? 10 : 8;

        return (
            <div className="table-register mb-4 mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 font-weight-bold">{label} <span className="text-muted">({holeFormat === 'betterball' ? 'Betterball' : 'Greensome'})</span></h6>
                </div>
                <table id="table-scorecard" className="table table-bordered table-striped table-card">
                    <thead>{renderTableHead(holeFormat)}</thead>
                    <tbody>
                        {holeIndices.map((idx) => renderRow(idx, gameDetails.TeeBoxes.ScoreCard[idx]))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-light font-weight500">
                            <td className='text-center'>{label === 'OUT (1-9)' ? 'OUT' : 'IN'}</td>
                            <td className='text-center'>
                                {holeIndices.reduce((sum, i) => sum + (gameDetails.TeeBoxes.ScoreCard[i]?.Par || 0), 0)}
                            </td>
                            {Array.from({ length: colCount - 3 }).map((_, i) => <td key={i} className='text-center'></td>)}
                            <td className='text-center font-weight-bold'>{footerScore}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };
    //#endregion

    if (!gameDetails || !scores) return null;

    return (<>
        <div className="kt-portlet kt-portlet--mobile padding-lr20">
            <div className="kt-portlet__head kt-portlet__head--lg d-block padding-lr0 border-0">

                <div id="roundInfoPanel" className="kt-notification margin-t10 mb-3" style={{ backgroundColor: "rgb(247, 248, 250)" }}>
                    <div className="kt-notification__item d-block">
                        <div className="row">
                            <div className="col-3">
                                <div className="kt-user-card-v2 mb-2">
                                    <div className="kt-user-card-v2__pic">
                                        <img id="txtTournamentImg" src={tournamentDetails?.Icon} alt="tournament-logo" />
                                    </div>
                                    <div className="kt-user-card-v2__details ">
                                        <span id="txtTournament" className="kt-user-card-v2__name text-clip"> {tournamentDetails?.Name} </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtRound"> Round {selectedPlayer?.Round} </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center">
                                <div className="kt-user-card-v2 ">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name ">Date </span>
                                        <span className="kt-user-card-v2__desc">
                                            <span id="txtStartDate">{moment(tournamentDetails?.StartDate).format('DD-MMM-YYYY')}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center">
                                <div className="kt-user-card-v2">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name ">Format </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtFormat">{format?.Format || 'Betterball & Greensome'}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {!!tournamentRound && tournamentRound?.length > 0 && (
                                <div className="col-2 d-flex-center">
                                    <div className="kt-user-card-v2 mb-2">
                                        <div className="kt-user-card-v2__details ">
                                            <span className="kt-user-card-v2__name ">Tee Of Option </span>
                                            <span className="kt-user-card-v2__desc ">
                                                <span id="txtTeeOption">{format?.TeeOffOption}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="col-1 d-flex-center">
                                <div className="kt-user-card-v2 ">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name " id="lbltxtSlot"> Slot </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtSlot">{selectedPlayer.Slot}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center justify-content-end">
                                {!!pointData && pointData.length > 0 && (
                                    <button id="btnPoint" type="button" className="btn btn-secondary btn-elevate btn-pill btn-sm m-0 mr-2"
                                        onClick={() => setShowPointModal(true)}>
                                        <i className="la la-list"></i> Point
                                    </button>
                                )}
                                {/* {!gameDetails.Card.length > 0 && (
                                    <button id="btnSwapFormat" type="button" className="btn btn-outline-brand btn-elevate btn-pill btn-sm m-0"
                                        onClick={swapFormats} title="Swap which nine plays Betterball vs Greensome">
                                    <i className="la la-exchange"></i> Swap 9s
                                    </button>
                                )} */}
                            </div>
                        </div>

                        <hr />
                        <div className="row">
                            <div className="col-12">
                                <small className="text-muted">
                                    Front 9 (Holes 1-9): <strong>{frontFormat === 'betterball' ? 'Betterball' : 'Greensome'}</strong>
                                    &nbsp;|&nbsp;
                                    Back 9 (Holes 10-18): <strong>{backFormat === 'betterball' ? 'Betterball' : 'Greensome'}</strong>
                                </small>
                            </div>
                        </div>
                        <hr />

                        <div className="row">
                            <div className="col-9">
                                <label className="kt-checkbox kt-checkbox--bold kt-checkbox--success mb-0">
                                    <input
                                        value="verify"
                                        name='verify'
                                        checked={scorecardStatus == 'verify'}
                                        onChange={() => toggleStatus('verify')}
                                        type="checkbox"
                                        className="form-verify" />
                                    Verify Scorecard <span></span>
                                </label>
                            </div>
                            <div className="col-3 text-right">
                                <div className="kt-checkbox-inline">
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--danger mb-0 mr-2">
                                        <input
                                            value="disqualify"
                                            name='disqualify'
                                            checked={scorecardStatus == 'disqualify'}
                                            onChange={() => toggleStatus('disqualify')}
                                            type="checkbox"
                                            className="form-disqualify" />
                                        Disqualify <span></span>
                                    </label>
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--brand mb-0">
                                        <input
                                            value="withdraw"
                                            checked={scorecardStatus == 'withdraw'}
                                            onChange={() => toggleStatus('withdraw')}
                                            name='withdraw'
                                            type="checkbox"
                                            className="form-withdraw" />
                                        Withdraw <span></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        {(scorecardStatus == 'withdraw' || scorecardStatus == 'disqualify') && (
                            <div className="row justify-content-end mt-2" id="pnlComment">
                                <div className="col-4 text-right">
                                    <textarea
                                        name="txtComment"
                                        id="txtComment"
                                        className="form-control"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        cols="3" rows="2" placeholder="Comments"
                                    ></textarea>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {renderNineTable(FRONT_HOLES, 'OUT (1-9)', footerFrontScore)}
                {renderNineTable(BACK_HOLES, 'IN (10-18)', footerBackScore)}

                <div className="row">
                    <div className="col-12 text-right">
                        <h5>Total: <span id="lblGrandTotal">{footerTotalScore}</span></h5>
                    </div>
                </div>
            </div>

            <div className="kt-portlet__foot">
                <div className="kt-form__actions">
                    <div className="row">
                        <div className="col-lg-12 ml-lg-auto text-center">
                            {(selectedPlayer.IsVerified == 'Y' || selectedPlayer.IsVerified == 'WD' || selectedPlayer.IsVerified == 'DQ') && (
                                <button id="btnEmailCard" type="button" className="btn btn-info mr-2" onClick={sendScorecard}><i className="fa fa fa-envelope bigger-110"></i> Email Card </button>
                            )}
                            <button id="btnDelete" type="button" className="btn btn-danger d-inline-block mr-4" onClick={deleteScoreCard}> Delete </button>
                            <button id="btnSave" type="button" className="btn btn-success d-inline-block mr-2" onClick={saveScorecard}> Save </button>
                            <button id="btnBack" type="button" className="btn btn-secondary" onClick={closeDrawer}> Close </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>);
}

export default BetterballGreensome;